import { useCallback, useEffect, useState } from "react";
import type { Pipeline, PipelineObjectType } from "./usePipelines";

export interface PipelineCanvasItem {
  portalId: string;
  objectType: PipelineObjectType;
  pipelineId: string;
  positionX: number;
  positionY: number;
}

interface ApiResponse {
  items: {
    portal_id?: string;
    portalId?: string;
    object_type?: string;
    objectType?: string;
    pipeline_id?: string;
    pipelineId?: string;
    position_x?: number;
    positionX?: number;
    position_y?: number;
    positionY?: number;
  }[];
  definitions: {
    objectType: PipelineObjectType;
    pipelineId: string;
    definition?: Pipeline;
    error?: string;
  }[];
}

export interface PipelineCanvasState {
  items: PipelineCanvasItem[];
  definitions: Record<string, Pipeline | undefined>;
  loading: boolean;
  error: string | null;
}

function defKey(objectType: PipelineObjectType, pipelineId: string): string {
  return `${objectType}:${pipelineId}`;
}

async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    const b = body as { error?: string; message?: string };
    throw new Error(b.error ?? b.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

function asObjectType(value: unknown): PipelineObjectType {
  return value === "tickets" ? "tickets" : "deals";
}

export function usePipelineCanvas(portalId: string | null) {
  const [state, setState] = useState<PipelineCanvasState>({
    items: [],
    definitions: {},
    loading: false,
    error: null,
  });

  const reload = useCallback(async () => {
    if (!portalId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getJson<ApiResponse>(
        `/api/pipeline-canvas/${portalId}`,
      );
      const items: PipelineCanvasItem[] = data.items.map((r) => ({
        portalId: (r.portalId ?? r.portal_id) as string,
        objectType: asObjectType(r.objectType ?? r.object_type),
        pipelineId: (r.pipelineId ?? r.pipeline_id) as string,
        positionX: (r.positionX ?? r.position_x ?? 0) as number,
        positionY: (r.positionY ?? r.position_y ?? 0) as number,
      }));
      const definitions: Record<string, Pipeline | undefined> = {};
      for (const d of data.definitions) {
        definitions[defKey(d.objectType, d.pipelineId)] = d.definition;
      }
      setState({ items, definitions, loading: false, error: null });
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : "Unknown error",
      }));
    }
  }, [portalId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const add = useCallback(
    async (objectType: PipelineObjectType, pipelineId: string) => {
      if (!portalId) return;
      await getJson(`/api/pipeline-canvas/${portalId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ objectType, pipelineId }),
      });
      await reload();
    },
    [portalId, reload],
  );

  const remove = useCallback(
    async (objectType: PipelineObjectType, pipelineId: string) => {
      if (!portalId) return;
      await getJson(
        `/api/pipeline-canvas/${portalId}/${objectType}/${pipelineId}`,
        { method: "DELETE" },
      );
      await reload();
    },
    [portalId, reload],
  );

  const updatePosition = useCallback(
    async (
      objectType: PipelineObjectType,
      pipelineId: string,
      x: number,
      y: number,
    ) => {
      if (!portalId) return;
      setState((s) => ({
        ...s,
        items: s.items.map((it) =>
          it.objectType === objectType && it.pipelineId === pipelineId
            ? { ...it, positionX: x, positionY: y }
            : it,
        ),
      }));
      await getJson(
        `/api/pipeline-canvas/${portalId}/${objectType}/${pipelineId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ x, y }),
        },
      );
    },
    [portalId],
  );

  return { ...state, reload, add, remove, updatePosition };
}
